import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ListFilter, PanelBottomClose } from 'lucide-react';
import './line-item-toolbar.css';
import { TooltipButton } from './TooltipButton';

export type LineItemToolbarAction = {
  id: string;
  label: string;
  tooltip?: string;
  icon?: ReactNode;
  disabled?: boolean;
  onSelect: () => void;
};
type OpenMenu = { kind: 'filter'; owner: string; focus: 'none' | 'first' | 'last' };

/** Presentation only: menus call the same supplied owners and never submit an
 * editor merely because the operator opened or dismissed a menu. */
export function LineItemToolbar({
  elementRef,
  owner,
  total,
  issueCount,
  issuesOnly,
  onFilter,
  title,
  status,
  actions,
  onCollapse,
  collapseLabel = 'Collapse line items',
}: {
  elementRef: RefObject<HTMLDivElement | null>;
  owner: string;
  total: number;
  issueCount: number;
  issuesOnly: boolean;
  onFilter: (issuesOnly: boolean) => void;
  title?: ReactNode;
  status?: ReactNode;
  actions?: readonly LineItemToolbarAction[];
  onCollapse?: () => void;
  collapseLabel?: string;
}) {
  const [opened, setOpened] = useState<OpenMenu | null>(null);
  const popup = useRef<HTMLDivElement>(null);
  const filter = useRef<HTMLButtonElement>(null);
  const menuAnchor = useRef<DOMRect | null>(null);
  const menuId = useId();
  const menu = opened?.owner === owner ? opened : null;
  const trigger = () => filter.current;
  const close = (restore = false) => {
    if (restore && popup.current?.contains(document.activeElement))
      trigger()?.focus({ preventScroll: true });
    setOpened(null);
  };
  const open = (kind: OpenMenu['kind'], focus: OpenMenu['focus']) =>
    setOpened({
      kind,
      owner,
      focus: focus === 'none' && popup.current?.contains(document.activeElement) ? 'first' : focus,
    });
  useLayoutEffect(() => {
    if (opened && opened.owner !== owner) {
      if (popup.current?.contains(document.activeElement))
        filter.current?.focus({ preventScroll: true });
      setOpened(null);
    }
  }, [owner]);
  useLayoutEffect(() => {
    if (!menu || !popup.current) return;
    const anchor = trigger();
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect(),
      box = popup.current.getBoundingClientRect();
    menuAnchor.current = rect;
    popup.current.style.left = `${Math.max(8, Math.min(rect.left, innerWidth - box.width - 8))}px`;
    popup.current.style.top = `${Math.max(8, Math.min(rect.bottom + box.height + 6 <= innerHeight ? rect.bottom + 4 : rect.top - box.height - 4, innerHeight - box.height - 8))}px`;
    if (menu.focus !== 'none') {
      const items = [...popup.current.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
      (menu.focus === 'last' ? items.at(-1) : items[0])?.focus({ preventScroll: true });
    }
  }, [menu]);
  useEffect(() => {
    if (!menu) return;
    const outside = (event: PointerEvent | FocusEvent) => {
      if (
        event.target instanceof Node &&
        !popup.current?.contains(event.target) &&
        !filter.current?.contains(event.target)
      )
        close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close(true);
    };
    const moved = (event: Event) => {
      if (event.target instanceof Node && popup.current?.contains(event.target)) return;
      // A scroll queued before the click may arrive after the menu opens.
      // Dismiss only when scrolling actually moved its anchor, not for an
      // unrelated pane or a completed scroll-to-button operation.
      if (event.type === 'scroll') {
        const before = menuAnchor.current,
          current = trigger()?.getBoundingClientRect();
        if (
          before &&
          current &&
          Math.abs(before.left - current.left) < 1 &&
          Math.abs(before.top - current.top) < 1 &&
          Math.abs(before.bottom - current.bottom) < 1
        )
          return;
      }
      close(true);
    };
    window.addEventListener('pointerdown', outside, true);
    window.addEventListener('focusin', outside);
    window.addEventListener('keydown', escape, true);
    window.addEventListener('scroll', moved, true);
    window.addEventListener('resize', moved);
    return () => {
      window.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('focusin', outside);
      window.removeEventListener('keydown', escape, true);
      window.removeEventListener('scroll', moved, true);
      window.removeEventListener('resize', moved);
    };
  }, [menu]);
  const triggerKeys = (kind: OpenMenu['kind'], event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    event.stopPropagation();
    open(kind, event.key === 'ArrowUp' ? 'last' : 'first');
  };
  const choose = (action: () => void) => {
    close(true);
    action();
  };
  return (
    <div
      ref={elementRef}
      className={`line-ledger-toolbar${total > 0 ? ' line-review-progress' : ''}`}
      role="group"
      aria-label="Line item controls"
    >
      <div className="line-ledger-filter-group">
        {title && <span className="line-ledger-toolbar-title">{title}</span>}
        {total > 0 && (
          <button
            ref={filter}
            type="button"
            className="line-ledger-filter"
            aria-label="Filter line items"
            aria-haspopup="menu"
            aria-expanded={menu?.kind === 'filter'}
            aria-controls={menu?.kind === 'filter' ? menuId : undefined}
            onMouseDown={(event) => event.preventDefault()}
            onKeyDown={(event) => triggerKeys('filter', event)}
            onClick={(event) =>
              menu?.kind === 'filter'
                ? close(true)
                : open('filter', event.detail === 0 ? 'first' : 'none')
            }
          >
            <ListFilter size={15} />
            <span>{issuesOnly ? 'To check' : 'All rows'}</span>
            <b>{issuesOnly ? issueCount : total}</b>
          </button>
        )}
      </div>
      <div className="line-ledger-toolbar-status">{status}</div>
      <div className="line-ledger-primary-actions">
        {actions?.map((action) => (
          <TooltipButton
            key={action.id}
            type="button"
            className="line-ledger-action"
            aria-label={action.label}
            tooltip={action.tooltip ?? action.label}
            disabled={action.disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={action.onSelect}
          >
            {action.icon ?? <span>{action.label}</span>}
          </TooltipButton>
        ))}
        {onCollapse && (
          <TooltipButton
            type="button"
            className="ledger-collapse"
            onMouseDown={(event) => event.preventDefault()}
            aria-label={collapseLabel}
            tooltip={collapseLabel}
            onClick={onCollapse}
          >
            <PanelBottomClose size={18} />
          </TooltipButton>
        )}
      </div>
      {menu &&
        createPortal(
          <div
            ref={popup}
            id={menuId}
            role="menu"
            aria-label="Filter line items"
            className="line-ledger-menu"
            onMouseDown={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              event.stopPropagation();
              const items = [
                ...(popup.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ??
                  []),
              ];
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              let next: number;
              if (event.key === 'ArrowDown') next = (index + 1) % items.length;
              else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
              else if (event.key === 'Home') next = 0;
              else if (event.key === 'End') next = items.length - 1;
              else if (event.key === 'Tab') {
                close(true);
                return;
              } else return;
              event.preventDefault();
              items[next]?.focus({ preventScroll: true });
            }}
          >
            <>
              <button
                type="button"
                role="menuitemradio"
                tabIndex={-1}
                aria-checked={!issuesOnly}
                onClick={() => choose(() => onFilter(false))}
              >
                <span>All rows</span>
                <b>{total}</b>
                {!issuesOnly && <Check size={15} />}
              </button>
              <button
                type="button"
                role="menuitemradio"
                tabIndex={-1}
                aria-checked={issuesOnly}
                disabled={issueCount === 0}
                onClick={() => choose(() => onFilter(true))}
              >
                <span>Arithmetic issues</span>
                <b>{issueCount}</b>
                {issuesOnly && <Check size={15} />}
              </button>
            </>
          </div>,
          document.body,
        )}
    </div>
  );
}
